/**
 * **What the targets endpoint's answer asserts** — one rule, two readers.
 *
 * `/indicators/{id}/targets` does not answer "the target is N". It answers with a trajectory
 * row, two flags and a scope, and the three of them together decide whether the number on
 * screen is IFDC's commitment, the programme commitment apportioned by a country's share of
 * the actuals, or the year's own cumulative actual standing in for a commitment that does not
 * exist yet (the client's 2026-09-15 instruction for the pre-2026 years).
 *
 * That rule was written once, inside the Overview's `targetChart`, where it sat between a
 * per-country row loop and a bar-chart shape. The 16 Sep re-skin gave the indicator panel a
 * Target tile and a "Progress to target" gauge, which need the same three-way answer and none
 * of the chart — and two implementations of "what counts as this indicator's target" is
 * exactly how the panel and the Overview would come to disagree about B.2.1 in the same year.
 * So the rule is here, pure, and both call it.
 *
 * **What is deliberately *not* here** is the Overview's own client-side apportioning
 * (programme target × country achieved ÷ programme achieved). That is a second answer derived
 * from numbers the Overview happens to be holding for four countries at once, not a reading of
 * this response, and the indicator panel must not invent it for a region or a watershed — the
 * §F5.8 rungs below a country have no target of their own and say so with an em dash.
 */
import type { TargetsResponse, ValueMode } from './api';

/**
 * Where the target came from — which is the label, not a detail.
 *
 * - `trajectory` — IFDC's own commitment for the year and scope.
 * - `apportioned` — the programme commitment, shared out by the server on the country's share
 *   of the cumulative actuals, because IFDC has allocated none of it across the four
 *   countries. Never presented as an IFDC allocation.
 * - `actuals` — the year's own achievement standing in for a commitment (pre-2026). Progress
 *   is then 100 % by construction, and saying "target" without saying "(actuals)" would be
 *   claiming a goal was met that was never set.
 * - `none` — no commitment reaches this year and scope.
 */
export type TargetKind = 'trajectory' | 'apportioned' | 'actuals' | 'none';

export interface ResolvedTarget {
  /** The figure to compare `achieved` against, or `null` when there is nothing to compare to. */
  target: number | null;
  kind: TargetKind;
}

const NONE: ResolvedTarget = { target: null, kind: 'none' };

/**
 * The target the response asserts **for the scope it answered for**.
 *
 * `achieved` is the figure the caller is showing, in the same reading as `mode` — it is only
 * read for the `actuals` case, where the target *is* that figure.
 *
 * `mode` picks which column of the trajectory row is comparable: a cumulative headline is
 * compared against `cumulative_target` and an annual one against `annual_target`. Comparing
 * a year's increment against a running total is how a panel reports 6 % progress on an
 * indicator the Overview shows at 94 %, and both would be reading the same two numbers.
 */
export function resolveTarget(
  targets: TargetsResponse | null | undefined,
  achieved: number | null,
  mode: ValueMode = 'cumulative',
): ResolvedTarget {
  if (!targets) return NONE;
  // Checked before `target`, because the actuals answer carries a row whose `annual_target`
  // is not a commitment — the flag is the sentence, the row is its packaging.
  if (targets.actuals_as_target) return { target: achieved, kind: 'actuals' };
  const row = targets.target;
  if (!row) return NONE;
  const figure = mode === 'cumulative' ? row.cumulative_target : row.annual_target;
  return {
    target: typeof figure === 'number' && Number.isFinite(figure) ? figure : null,
    kind: targets.apportioned ? 'apportioned' : 'trajectory',
  };
}
